// Diagnóstico para o "Reportar bug": dois buffers circulares em memória (últimas chamadas
// da API e últimos erros do console) e o coletor que os alimenta. Nada sai do navegador
// até o seller enviar um reporte, e o que entra aqui é só metadado: nunca corpo de request
// ou de resposta, nunca query string de chamada, nunca header.

import { redactSecrets } from './redact.ts';

export const BUFFER_SIZE = 30;

const MESSAGE_MAX = 500;
const STACK_MAX = 1000;
const STACK_LINES = 4;

export interface Ring<T> {
  push: (item: T) => void;
  snapshot: () => T[];
  clear: () => void;
}

/** Guarda os últimos `max` itens; o mais antigo sai primeiro. */
export function createRing<T>(max: number): Ring<T> {
  let items: T[] = [];
  return {
    push(item) {
      items.push(item);
      if (items.length > max) {
        items = items.slice(items.length - max);
      }
    },
    snapshot: () => [...items],
    clear() {
      items = [];
    },
  };
}

export interface ApiCallEntry {
  method: string;
  path: string;
  status: number;
  duration_ms: number;
  request_id: string | null;
  at: string;
}

export type ConsoleLevel = 'error' | 'warn' | 'unhandledrejection' | 'window_error';

export interface ConsoleEntry {
  level: ConsoleLevel;
  message: string;
  stack: string | null;
  at: string;
}

export const apiCalls = createRing<ApiCallEntry>(BUFFER_SIZE);
export const consoleErrors = createRing<ConsoleEntry>(BUFFER_SIZE);

/**
 * `path` é o caminho pedido à API, sem a query (ids públicos podem estar nele, valores de
 * filtro não). `status` 0 = a requisição falhou antes de ter resposta.
 */
export function recordApiCall(entry: Omit<ApiCallEntry, 'at'>, now: Date = new Date()): void {
  apiCalls.push({ ...entry, path: entry.path.split('?')[0] ?? entry.path, at: now.toISOString() });
}

function truncate(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1)}…`;
}

/** Mensagem curta e sem segredo. Objeto vira o nome do tipo: não serializa conteúdo. */
export function describeValue(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value instanceof Error) {
    return `${value.name}: ${value.message}`;
  }
  if (typeof value === 'number' || typeof value === 'boolean' || value === null) {
    return String(value);
  }
  return Object.prototype.toString.call(value);
}

function stackOf(value: unknown): string | null {
  if (!(value instanceof Error) || value.stack === undefined) {
    return null;
  }
  const lines = value.stack.split('\n').slice(0, STACK_LINES).join('\n');
  return truncate(redactSecrets(lines), STACK_MAX);
}

export function recordConsole(
  level: ConsoleLevel,
  args: readonly unknown[],
  now: Date = new Date(),
): void {
  const message = args.map(describeValue).join(' ');
  const errorArg = args.find((arg) => arg instanceof Error);
  consoleErrors.push({
    level,
    message: truncate(redactSecrets(message), MESSAGE_MAX),
    stack: stackOf(errorArg),
    at: now.toISOString(),
  });
}

let installed = false;

/**
 * Chamar uma vez na subida do app. Embrulha `console.error`/`console.warn` (a chamada
 * original continua acontecendo antes de qualquer registro) e escuta `error` e
 * `unhandledrejection`. O coletor nunca escreve no console: um erro dele não pode virar
 * outro erro coletado.
 */
export function installDiagnostics(target: Window & typeof globalThis = window): void {
  if (installed) {
    return;
  }
  installed = true;

  for (const level of ['error', 'warn'] as const) {
    const original = target.console[level].bind(target.console);
    target.console[level] = (...args: unknown[]) => {
      original(...args);
      try {
        recordConsole(level, args);
      } catch {
        // ponytail: coletor mudo de propósito; log aqui reentraria no próprio wrapper.
      }
    };
  }

  target.addEventListener('error', (event) => {
    try {
      recordConsole('window_error', [event.error ?? event.message]);
    } catch {
      // idem
    }
  });
  target.addEventListener('unhandledrejection', (event) => {
    try {
      recordConsole('unhandledrejection', [event.reason]);
    } catch {
      // idem
    }
  });
}

/** Só pra teste: devolve o estado de instalação ao inicial. */
export function resetDiagnosticsForTest(): void {
  installed = false;
  apiCalls.clear();
  consoleErrors.clear();
}
